// Estudos para quem participa de um grupo de estudos (0.71, texto 09 Parte J).
// Mostra SOMENTE o fichário que o dono compartilhou, para ver na tela: sem editar, sem baixar, sem imprimir,
// sem exportar e sem compartilhar. Quem não tem a aba vê os botões de criar, editar e cronometrar normais; ao tocar,
// aparece o convite gentil (components/ConvidarAssinar), no máximo uma vez por sessão; depois, só um ✨ no botão.
// Tudo acontece nesta mesma tela (lista > matéria > página), sem janelas por cima, para funcionar igual no iPhone e no Android.
import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, BackHandler, Image, Linking, Pressable, Text, View } from 'react-native';
import type { Anexo, BlocoPag, Fichario, Materia, Pagina, Quadro as Q } from '../store/types';
import { Btn, Screen } from '../components/ui';
import { C, alpha, useCores } from '../components/theme';
import { abrirGrupos } from '../components/Grupos';
import { useConvite } from '../components/ConvidarAssinar';
import Paywall from './Paywall';
import { DesenhoView, areaUsada } from '../components/Desenho';
import { temConteudo } from '../components/Quadro';
import { LeitorPdf } from '../components/PdfVisual';
import { useMeusGrupos } from '../lib/grupos';
import { gruposConvidadoEstudos, deB64, imagemDoGrupoJa, useFicharioDoGrupo, useImagensDoGrupo, verDoGrupo, type FicharioDoGrupo } from '../lib/estudosGrupo';
import { blocosDaPagina } from '../store/estudos';
import { useBotaoNovo } from '../lib/botaoNovo';
import { nomeAnexo } from '../lib/nomeArquivo';
import { pdfTemporario } from '../lib/pdfTemp';
import { contarPaginas } from '../lib/pdfEdit';
import { temRenderizador } from '../lib/pdfRender';
import { semBloqueio } from '../auth/AuthContext';
import { brDateTime } from '../lib/date';
import { t } from '../lib/i18n';

type Onde = { gid: string; mid?: string; pid?: string; arquivos?: boolean } | null;
type Visor = { tipo: 'imagem' | 'pdf'; nome: string; uri?: string; paginas?: number; apagar?: () => void; erro?: string } | null;

/** Imagem do grupo no lugar da original (o quadro e os blocos mostram pelo "dado" já carregado na memória). */
const comImagem = (a: Anexo): Anexo => { const d = imagemDoGrupoJa(a.gp); return d ? { ...a, dado: d } : a; };
const quadroComImagens = (q?: Q | null): Q | null | undefined => (q ? { ...q, imagens: (q.imagens ?? []).map(im => ({ ...im, arq: comImagem(im.arq) })) } : q);
const ehPdf = (a: Anexo) => /pdf/i.test(a.mime ?? '') || /\.pdf$/i.test(a.nome ?? '');
const ehImagem = (a: Anexo) => (a.mime ?? '').startsWith('image/') || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.nome ?? '');

/** assinante: a pessoa tem a aba Estudos (veio de dentro de Estudos); então não há convite, só a leitura. */
export default function EstudosConvidado({ onBack, item, assinante }: { onBack: () => void; item?: string; assinante?: boolean }) {
  const { corDe } = useCores();
  const cor = corDe('estudos');
  const grupos = gruposConvidadoEstudos(useMeusGrupos().lista);
  const { lista, semServidor } = useFicharioDoGrupo();
  useImagensDoGrupo();
  const conv = useConvite('estudos');
  const [abertos, setAbertos] = useState<string[]>([]);
  const [onde, setOnde] = useState<Onde>(null);
  const [visor, setVisor] = useState<Visor>(null);
  const convite = (botao: string) => () => conv.pedir(botao);
  const rot = (texto: string, botao: string) => texto + conv.brilho(botao);
  useBotaoNovo(assinante ? null : { rotulo: t('Novo fichário'), fn: () => conv.pedir('novoFichario') });

  // veio de um grupo (👥 > "Abrir o fichário do grupo"): já abre o fichário dele
  useEffect(() => { if (item?.startsWith('grupo:')) setAbertos(a => [...a, item.slice(6)]); }, [item]);
  const doGrupo = (gid: string) => lista?.find(x => x.grupo_id === gid);
  const fechaVisor = () => { visor?.apagar?.(); setVisor(null); };
  useEffect(() => () => { visor?.apagar?.(); }, [visor]);
  const subir = () => {
    if (visor) return fechaVisor();
    if (!onde) return onBack();
    if (onde.pid || onde.arquivos) return setOnde({ gid: onde.gid, mid: onde.mid });
    if (onde.mid) return setOnde(null);
    setOnde(null);
  };
  // botão voltar do Android: sobe um nível (página > matéria > lista)
  useEffect(() => {
    const s = BackHandler.addEventListener('hardwareBackPress', () => { if (!onde && !visor) return false; subir(); return true; });
    return () => s.remove();
  });

  async function verArquivo(a: Anexo) {
    if (!a.gp) return Alert.alert(a.nome || t('Arquivo'), t('Este arquivo está guardado só no dispositivo ou no Drive de quem compartilhou, por isso não aparece aqui.'));
    if (!ehPdf(a) && !ehImagem(a)) return Alert.alert(a.nome || t('Arquivo'), t('No grupo de estudos, imagens e PDFs abrem aqui na tela. Este tipo de arquivo não tem visualização dentro do app.'));
    setVisor({ tipo: ehPdf(a) ? 'pdf' : 'imagem', nome: nomeAnexo(a) });
    try {
      const v = await verDoGrupo(a.gp);
      if (!ehPdf(a)) return setVisor({ tipo: 'imagem', nome: nomeAnexo(a), uri: v.dado });
      const paginas = await contarPaginas(deB64(v.dado.slice(v.dado.indexOf(',') + 1))).catch(() => 1);
      const tmp = pdfTemporario(v.dado);
      setVisor({ tipo: 'pdf', nome: nomeAnexo(a), uri: tmp.uri, paginas, apagar: tmp.apagar });
    } catch {
      setVisor({ tipo: ehPdf(a) ? 'pdf' : 'imagem', nome: nomeAnexo(a), erro: t('Não deu para mostrar agora. Confira a internet e tente de novo.') });
    }
  }
  const abrirLink = (url: string) => semBloqueio(() => Linking.openURL(url)).catch(() => {});

  if (conv.conhecer) return <Paywall id="estudos" onBack={conv.fecharConhecer} />;

  // ─── Visor de imagem ou PDF (só na tela) ───
  if (visor) return (
    <Screen title={visor.nome} onBack={fechaVisor} scroll={visor.tipo !== 'pdf' || !visor.uri}>
      {!visor.uri && !visor.erro && <ActivityIndicator color={cor} style={{ marginTop: 40 }} />}
      {!!visor.erro && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 30 }}>{visor.erro}</Text>}
      {visor.tipo === 'imagem' && !!visor.uri && <Image source={{ uri: visor.uri }} resizeMode="contain" style={{ width: '100%', aspectRatio: 0.75, borderRadius: 12, backgroundColor: C.card }} />}
      {visor.tipo === 'pdf' && !!visor.uri && (temRenderizador()
        ? <View style={{ flex: 1, marginHorizontal: -16 }}><LeitorPdf uri={visor.uri} total={visor.paginas ?? 1} cor={cor} /></View>
        : <Text style={{ color: C.sub, textAlign: 'center', marginTop: 30 }}>{t('Para ver PDFs aqui na tela, instale a versão mais nova do app pela loja.')}</Text>)}
    </Screen>
  );

  const fg = onde ? doGrupo(onde.gid) : undefined;
  const f = fg?.fichario;
  const m = f && onde?.mid ? f.materias.find(x => x.id === onde.mid) : undefined;
  const p = m && onde?.pid ? m.paginas.find(x => x.id === onde.pid) : undefined;
  const fc = f?.cor ?? cor;

  // ─── Página (só leitura) ───
  if (fg && f && m && p) return (
    <Screen title={p.titulo || t('Página')} onBack={subir} right={assinante ? undefined :
      <Pressable onPress={convite('editar')} hitSlop={10}><Text style={{ color: fc, fontWeight: '700' }}>{rot(t('Editar'), 'editar')}</Text></Pressable>}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{f.icon} {f.nome} › {m.icon} {m.nome}</Text>
      {temConteudo(p.quadro) && (() => { const q = quadroComImagens(p.quadro)!; const a = areaUsada(q, 400); return <DesenhoView tracos={q.tracos} alt={a.alt} larg={a.larg} quadro={q} />; })()}
      {blocosDaPagina(p).map(b => <BlocoLeitura key={b.id} b={b} cor={fc} onArquivo={verArquivo} onLink={abrirLink} />)}
      {!temConteudo(p.quadro) && !blocosDaPagina(p).length && <Text style={{ color: C.sub }}>{t('Página em branco.')}</Text>}
      <Text style={{ color: C.mute, fontSize: 11 }}>{p.updatedAt ? t('Editada em {d}', { d: brDateTime(p.updatedAt) }) : ''}</Text>
    </Screen>
  );

  // ─── Arquivos da matéria ───
  if (fg && f && m && onde?.arquivos) {
    const arqs = arquivosDa(m);
    return (
      <Screen title={t('Arquivos de {nome}', { nome: m.nome })} onBack={subir}>
        {!arqs.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 20 }}>{t('Nenhum arquivo, imagem ou link nas páginas desta matéria.')}</Text>}
        {arqs.map(a => (
          <Pressable key={a.k} onPress={() => (a.url ? abrirLink(a.url) : a.arq && verArquivo(a.arq))}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
            <Text style={{ fontSize: 24 }}>{a.url ? '🔗' : a.arq && ehImagem(a.arq) ? '🖼️' : '📎'}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={2}>{a.nome}</Text>
              <Text style={{ color: C.sub, fontSize: 11 }}>{t('Página: {titulo}', { titulo: a.pagina })}</Text>
            </View>
          </Pressable>
        ))}
        {!assinante && <Btn cor={fc} outline label={rot('📎 ' + t('Adicionar arquivo'), 'arquivo')} onPress={convite('arquivo')} />}
      </Screen>
    );
  }

  // ─── Matéria ───
  if (fg && f && m) return (
    <Screen title={m.nome} onBack={subir} right={assinante ? undefined :
      <Pressable onPress={convite('editar')} hitSlop={10}><Text style={{ color: fc }}>{rot(t('Editar'), 'editar')}</Text></Pressable>}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <Text style={{ fontSize: 40 }}>{m.icon}</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{f.icon} {f.nome}{m.categoria ? ' · ' + t(m.categoria) : ''}</Text>
          <Text style={{ color: C.text, fontSize: 20, fontWeight: '800' }}>{m.nome}</Text>
        </View>
      </View>
      {!assinante && <Btn cor={fc} label={rot('▶ ' + t('Estudar agora'), 'estudar')} onPress={convite('estudar')} />}
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {!assinante && <Btn cor={fc} outline label={rot('📎 ' + t('Adicionar arquivo'), 'arquivo')} style={{ flex: 1 }} onPress={convite('arquivo')} />}
        <Btn cor={fc} outline label={'🗂️ ' + t('Ver arquivos') + (arquivosDa(m).length ? ` (${arquivosDa(m).length})` : '')} style={{ flex: 1 }} onPress={() => setOnde({ ...onde!, arquivos: true })} />
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, flex: 1 }}>{t('PÁGINAS')}</Text>
        {!assinante && <Pressable onPress={convite('novaPagina')} hitSlop={8}><Text style={{ color: fc, fontWeight: '700' }}>{rot('+ ' + t('Página'), 'novaPagina')}</Text></Pressable>}
      </View>
      {!m.paginas.length && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Nenhuma página nesta matéria ainda.')}</Text>}
      {m.paginas.map(pg => (
        <Pressable key={pg.id} onPress={() => setOnde({ gid: onde!.gid, mid: m.id, pid: pg.id })}
          style={{ flexDirection: 'row', gap: 10, backgroundColor: C.card, borderRadius: 14, padding: 12, alignItems: 'center' }}>
          {!assinante && (
            <Pressable hitSlop={8} onPress={convite('revisada')}
              style={{ width: 26, height: 26, borderRadius: 8, borderWidth: 2, borderColor: fc, alignItems: 'center', justifyContent: 'center' }}>
              {!!conv.brilho('revisada') && <Text style={{ fontSize: 10 }}>✨</Text>}
            </Pressable>
          )}
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '600' }}>{pg.titulo}</Text>
            <Text style={{ color: C.mute, fontSize: 10 }} numberOfLines={1}>{pg.updatedAt ? t('Editada em {d}', { d: brDateTime(pg.updatedAt) }) : ''}</Text>
          </View>
          <Text style={{ color: C.mute, fontSize: 18 }}>›</Text>
        </Pressable>
      ))}
    </Screen>
  );

  // ─── Lista: aviso do grupo e fichários compartilhados ───
  return (
    <Screen title={t('Estudos')} onBack={onBack} right={
      <Pressable onPress={() => abrirGrupos({ aba: 'estudos' })} accessibilityLabel={t('Grupos de estudo')} style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ fontSize: 18 }}>👥</Text></Pressable>}>
      {grupos.map(g => {
        const x = doGrupo(g.id);
        return (
          <View key={g.id} style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 16, padding: 12, gap: 4, borderWidth: 1, borderColor: alpha(cor, 0.35) }}>
            <Text style={{ color: C.text, fontWeight: '700' }}>👥 {t('Você está participando do grupo de {nome}', { nome: x?.dono_nome || g.nome })}</Text>
            {(x?.dono_nome ? g.nome !== x.dono_nome : false) && <Text style={{ color: C.sub, fontSize: 12 }}>{g.nome}</Text>}
            {lista && !x && <Text style={{ color: C.sub, fontSize: 12 }}>{semServidor ? t('O compartilhamento de fichários ainda está sendo preparado. Volte daqui a pouco.') : t('Assim que o fichário for compartilhado, ele aparece aqui.')}</Text>}
          </View>
        );
      })}
      {!assinante && <Btn cor={cor} outline label={rot('▶ ' + t('Sessão de estudo'), 'sessao')} onPress={convite('sessao')} />}
      {!assinante && (
        <Pressable onPress={convite('relatorio')} style={{ backgroundColor: C.card, borderRadius: 18, padding: 14, flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, flex: 1 }}>{t('ESTA SEMANA')}</Text>
          <Text style={{ color: cor, fontWeight: '700' }}>{rot('📊 ' + t('Relatório de estudos') + ' ›', 'relatorio')}</Text>
        </Pressable>
      )}
      {lista === null && <ActivityIndicator color={cor} />}
      {(lista ?? []).filter(x => grupos.some(g => g.id === x.grupo_id)).map(x => (
        <FicharioCartao key={x.grupo_id} x={x} cor={cor} aberto={abertos.includes(x.grupo_id)} assinante={assinante}
          onAbrir={() => setAbertos(a => (a.includes(x.grupo_id) ? a.filter(y => y !== x.grupo_id) : [...a, x.grupo_id]))}
          onMateria={mid => setOnde({ gid: x.grupo_id, mid })} convite={convite} rot={rot} />
      ))}
      {conv.tela}
    </Screen>
  );
}

function FicharioCartao({ x, cor, aberto, assinante, onAbrir, onMateria, convite, rot }: { x: FicharioDoGrupo; cor: string; aberto: boolean; assinante?: boolean;
  onAbrir: () => void; onMateria: (mid: string) => void; convite: (b: string) => () => void; rot: (txt: string, b: string) => string }) {
  const f: Fichario = x.fichario;
  const fc = f.cor ?? cor;
  return (
    <View style={{ backgroundColor: alpha(fc, 0.1), borderRadius: 20, borderWidth: 1, borderColor: alpha(fc, 0.35), overflow: 'hidden' }}>
      <Pressable onPress={onAbrir} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14 }}>
        <Text style={{ color: C.mute }}>{aberto ? '▾' : '▸'}</Text>
        <Text style={{ fontSize: 30 }}>{f.icon}</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{f.nome}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('{n} matéria(s)', { n: f.materias.length })}{!aberto && f.materias.length ? ' · ' + t('toque para abrir') : ''}</Text>
        </View>
      </Pressable>
      {aberto && (
        <View style={{ paddingHorizontal: 10, paddingBottom: 10, gap: 6 }}>
          {f.materias.map((m: Materia) => (
            <Pressable key={m.id} onPress={() => onMateria(m.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
              <Text style={{ fontSize: 22 }}>{m.icon}</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ color: C.text, fontWeight: '600' }}>{m.nome}</Text>
                <Text style={{ color: C.sub, fontSize: 11 }}>{t('{n} página(s)', { n: m.paginas.length })}</Text>
              </View>
              {!assinante && (
                <Pressable hitSlop={8} onPress={convite('estudar')} style={{ minWidth: 34, height: 34, borderRadius: 17, backgroundColor: fc, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6 }}>
                  <Text style={{ color: '#fff', fontSize: 14 }}>{rot('▶', 'estudar')}</Text>
                </Pressable>
              )}
            </Pressable>
          ))}
          {!f.materias.length && <Text style={{ color: C.sub, fontSize: 12, padding: 6 }}>{t('Nenhuma matéria neste fichário ainda.')}</Text>}
          {!assinante && <Pressable onPress={convite('novaMateria')} style={{ padding: 8 }}><Text style={{ color: fc, fontWeight: '600' }}>{rot(t('+ Matéria em {nome}', { nome: f.nome }), 'novaMateria')}</Text></Pressable>}
        </View>
      )}
    </View>
  );
}

/** Um bloco da página, só para ler. */
function BlocoLeitura({ b, cor, onArquivo, onLink }: { b: BlocoPag; cor: string; onArquivo: (a: Anexo) => void; onLink: (u: string) => void }) {
  const caixa = { backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6 } as const;
  switch (b.tipo) {
    case 'texto': return b.texto.trim() ? <View style={caixa}><Text style={{ color: C.text, fontSize: 15 }}>{b.texto}</Text></View> : null;
    case 'lista': case 'check': return (
      <View style={caixa}>
        {b.itens.filter(i => i.text.trim()).map(i => (
          <Text key={i.id} style={{ color: C.text, textDecorationLine: b.tipo === 'check' && i.done ? 'line-through' : 'none' }}>{b.tipo === 'check' ? (i.done ? '☑ ' : '☐ ') : '• '}{i.text}</Text>
        ))}
      </View>
    );
    case 'tabela': return (
      <View style={caixa}>
        {b.linhas.map((l, r) => <View key={r} style={{ flexDirection: 'row' }}>{l.map((v, c) => <Text key={c} style={{ flex: 1, borderWidth: 0.5, borderColor: C.line, color: C.text, padding: 4, fontSize: 13, fontWeight: r === 0 ? '700' : '400' }}>{v}</Text>)}</View>)}
      </View>
    );
    case 'link': return b.url ? (
      <Pressable onPress={() => onLink(b.url)} style={caixa}><Text style={{ color: cor }} numberOfLines={2}>🔗 {b.titulo || b.url}</Text></Pressable>
    ) : null;
    case 'imagem': {
      const d = imagemDoGrupoJa(b.arq.gp);
      return (
        <Pressable onPress={() => onArquivo(b.arq)} style={caixa}>
          {d ? <Image source={{ uri: d }} resizeMode="contain" style={{ width: '100%', aspectRatio: 4 / 3, borderRadius: 10 }} />
            : b.arq.gp ? <ActivityIndicator color={cor} /> : <Text style={{ color: C.sub }}>🖼️ {nomeAnexo(b.arq)}</Text>}
        </Pressable>
      );
    }
    case 'arquivo': return (
      <Pressable onPress={() => onArquivo(b.arq)} style={[caixa, { flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
        <Text style={{ fontSize: 22 }}>📎</Text>
        <Text style={{ color: C.text, flex: 1 }} numberOfLines={2}>{nomeAnexo(b.arq)}</Text>
        <Text style={{ color: cor, fontWeight: '700' }}>{t('Ver')}</Text>
      </Pressable>
    );
    case 'desenho': {
      const q = quadroComImagens(b.quadro);
      return <DesenhoView tracos={b.tracos} alt={b.alt} larg={b.larg} quadro={q ?? null} />;
    }
    default: return null;
  }
}

/** Arquivos, imagens e links das páginas da matéria (como em "Ver arquivos"). */
function arquivosDa(m: Materia) {
  const out: { k: string; pagina: string; nome: string; arq?: Anexo; url?: string }[] = [];
  for (const p of m.paginas as Pagina[]) for (const b of blocosDaPagina(p)) {
    if (b.tipo === 'arquivo' || b.tipo === 'imagem') out.push({ k: `${p.id}:${b.id}`, pagina: p.titulo, nome: nomeAnexo(b.arq), arq: b.arq });
    else if (b.tipo === 'link' && b.url) out.push({ k: `${p.id}:${b.id}`, pagina: p.titulo, nome: b.titulo || b.url, url: b.url });
  }
  const pos = new Map((m.ordemArq ?? []).map((k, i) => [k, i]));
  return out.map((x, i) => ({ x, i })).sort((a, b) => (pos.get(a.x.k) ?? 1e6 + a.i) - (pos.get(b.x.k) ?? 1e6 + b.i)).map(z => z.x);
}
